import { INotification } from "../../api/notificationAPI";

// Reconnects can redeliver the same row, and a fetch can race a stream
// event for it — always de-dupe by notification_uuid before prepending.
export const dedupePrependNotification = (
  items: INotification[],
  incoming: INotification,
): INotification[] => {
  if (items.some((item) => item.notification_uuid === incoming.notification_uuid)) {
    return items;
  }
  return [incoming, ...items];
};

export const applyReadState = (
  items: INotification[],
  uuids: string[],
): INotification[] => {
  const uuidSet = new Set(uuids);
  return items.map((item) =>
    uuidSet.has(item.notification_uuid) && item.is_read !== 1
      ? { ...item, is_read: 1, read_ts: new Date().toISOString() }
      : item,
  );
};

export const countUnreadAmong = (items: INotification[], uuids: string[]): number => {
  const uuidSet = new Set(uuids);
  return items.filter((item) => uuidSet.has(item.notification_uuid) && item.is_read === 0)
    .length;
};

// The dropdown only ever shows the unfiltered latest 20; the full page can
// be scoped to one module. A live event should only join a list it would
// have matched had it been fetched.
export const matchesActiveFilters = (
  notification: INotification,
  filters: { module?: string | null; is_read?: boolean | null } | null,
): boolean => {
  if (!filters) return true;
  if (filters.module && notification.module !== filters.module) return false;
  if (filters.is_read === true && notification.is_read !== 1) return false;
  if (filters.is_read === false && notification.is_read === 1) return false;
  return true;
};

export interface IModuleMeta {
  icon: string;
  label: string;
  color: string;
}

// Icons come from `module`, not `event_key` — the module list is stable,
// event keys keep growing.
const MODULE_META_MAP: Record<string, IModuleMeta> = {
  LEAD: { icon: "user-plus", label: "Lead", color: "#1e88e5" },
  BOOKING: { icon: "calendar", label: "Booking", color: "#8e24aa" },
  PAYMENT: { icon: "credit-card", label: "Payment", color: "#2e7d32" },
  SUPPORT: { icon: "life-buoy", label: "Support", color: "#ef6c00" },
  CUSTOMER: { icon: "users", label: "Customer", color: "#00897b" },
  OPPORTUNITY: { icon: "trending-up", label: "Opportunity", color: "#3949ab" },
  TASK: { icon: "check-square", label: "Task", color: "#6d4c41" },
  SYSTEM: { icon: "settings", label: "System", color: "#546e7a" },
  BROADCAST: { icon: "volume-2", label: "Announcement", color: "#d81b60" },
};

const toTitleCase = (value: string) =>
  value
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

export const getModuleMeta = (moduleName: string | null | undefined): IModuleMeta =>
  (moduleName && MODULE_META_MAP[moduleName]) || {
    icon: "bell",
    label: moduleName ? toTitleCase(moduleName) : "General",
    color: "#607d8b",
  };

export const getModuleIcon = (moduleName: string): string => getModuleMeta(moduleName).icon;

export const formatFullDateTime = (isoDate: string | null | undefined): string => {
  if (!isoDate) return "";
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const startOfDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

const getDayBucket = (isoDate: string): string => {
  const then = new Date(isoDate);
  if (Number.isNaN(then.getTime())) return "Earlier";
  const dayDiff = Math.round((startOfDay(new Date()) - startOfDay(then)) / 86400000);
  if (dayDiff <= 0) return "Today";
  if (dayDiff === 1) return "Yesterday";
  if (dayDiff < 7) return "This week";
  return "Earlier";
};

// The server already sorts newest first, so buckets come out in order —
// this only splits the list, it never re-sorts it.
export const groupNotificationsByDay = (
  items: INotification[],
): { label: string; items: INotification[] }[] => {
  const groups: { label: string; items: INotification[] }[] = [];
  items.forEach((item) => {
    const label = getDayBucket(item.insert_ts);
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.items.push(item);
    else groups.push({ label, items: [item] });
  });
  return groups;
};

export const formatRelativeTime = (isoDate: string): string => {
  const then = new Date(isoDate).getTime();
  if (Number.isNaN(then)) return "";
  const diffMs = Date.now() - then;
  const diffSec = Math.max(0, Math.floor(diffMs / 1000));

  if (diffSec < 60) return "Just now";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay === 1) return "Yesterday";
  if (diffDay < 7) return `${diffDay}d`;
  return new Date(isoDate).toLocaleDateString();
};
